import type { ListApi, ListPagedResponseApi } from '../types/lists-api.types';
import type { CampaignList, ListsPagingResult } from '../types/lists.types';

const firstNonEmpty = (...values: Array<string | null | undefined>): string => {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return '';
};

const toCount = (value: number | null | undefined): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.max(0, value);
};

export const mapListApiToCampaignList = (item: ListApi | null | undefined): CampaignList | null => {
  if (!item || typeof item !== 'object') return null;

  const id = firstNonEmpty(item.id, item.itemId, item.ItemId);
  const name = firstNonEmpty(item.name, item.Name);
  if (!id && !name) return null;

  return {
    id: id || name,
    name,
    description: firstNonEmpty(item.description, item.Description),
    memberCount: toCount(item.memberCount ?? item.membersCount ?? item.MemberCount),
    createdAt:
      firstNonEmpty(item.createdAt, item.CreatedAt, item.createdDate, item.CreatedDate) || undefined,
    updatedAt: firstNonEmpty(item.updatedAt, item.UpdatedAt) || undefined,
  };
};

export const mapListItems = (rawItems: ListApi[] | null | undefined): CampaignList[] =>
  (rawItems ?? [])
    .map((item) => mapListApiToCampaignList(item))
    .filter((item): item is CampaignList => item !== null);

export const toListsPagingResult = (
  items: CampaignList[],
  totalCount: number,
  pageNo: number,
  pageSize: number,
  pageInfo?: ListPagedResponseApi['pageInfo']
): ListsPagingResult => {
  const resolvedPageSize = pageInfo?.limit ?? pageSize;
  const resolvedTotal = Number(pageInfo?.total ?? totalCount);
  const totalPages = resolvedPageSize > 0 ? Math.ceil(resolvedTotal / resolvedPageSize) : 0;

  return {
    items,
    totalCount: resolvedTotal,
    pageNo,
    pageSize: resolvedPageSize,
    totalPages,
    hasNextPage: pageInfo?.hasNext ?? pageNo < totalPages,
    hasPreviousPage: pageInfo?.hasPrev ?? pageNo > 1,
  };
};

export const formatListCreatedDate = (value?: string): string => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const formatMemberCountLabel = (count: number): string =>
  `${count.toLocaleString()} member${count === 1 ? '' : 's'}`;

export const formatListsSubtitle = (listCount: number, totalMemberships: number): string =>
  `${listCount.toLocaleString()} list${listCount === 1 ? '' : 's'} · ${totalMemberships.toLocaleString()} total memberships`;
